import {
  IExperienceMessageDenormalized,
  isContentBlock,
  isLayoutBlock
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { IconButton, Typography } from '@material-ui/core';
import { ChevronDown, ChevronUp, Mail } from 'lucide-react';
import moment from 'moment';
import React, { useState } from 'react';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { TruncatedContentSummary } from '../../../../../../features/Experiences/components/Builders/TaskBuilder/components/TruncatedContentSummary';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { now, toMoment } from '../../../../../../services/time';
import {
  Content,
  LayoutBlockComponent
} from '../../../../../features/Experience/components/Task';
import { archiveMessage } from '../../../../../features/Experience/service';
import { useProductAnalytics } from '../../../../../features/Experience/services/productAnalytics';
import {
  EmployeeAppLocalUpdates,
  useLocalUpdates
} from '../../services/localUpdateProvider';
import { WithBreakpoints } from '../Breakpoints';
import { EmployeeAppCard } from '../EmployeeAppCard';
import { RoundIconContainer } from '../RoundIconContainer';

export const MessageBody = ({ d }: { d: IExperienceMessageDenormalized }) => {
  return (
    <FlexContainerVertical fullWidth spacing={2}>
      {d.blocks.map((b) =>
        !isLayoutBlock(b) && b.parentLayoutElement ? null : (
          <React.Fragment key={b.id}>
            {isContentBlock(b) && <Content key={b.id} d={b} />}
            {isLayoutBlock(b) && (
              <LayoutBlockComponent
                taskId=""
                b={b}
                blocks={d.blocks}
                highlight={false}
              />
            )}
          </React.Fragment>
        )
      )}
    </FlexContainerVertical>
  );
};

const updateMarkedAsRead = (
  localUpdates: EmployeeAppLocalUpdates,
  instanceAccessToken: string,
  messageId: string,
  read: boolean
) => {
  return {
    ...localUpdates,
    [instanceAccessToken]: {
      ...localUpdates[instanceAccessToken],
      msgs: {
        ...(localUpdates[instanceAccessToken]?.msgs || {}),
        [messageId]: { markedAsRead: { at: now(), read } }
      }
    }
  };
};

export const MessageArchiveButton = ({
  d,
  instanceAccessToken,
  onArchive,
  onUnarchive
}: {
  d: IExperienceMessageDenormalized;
  instanceAccessToken: string;
  // For tracking callbacks
  onArchive?: () => void;
  onUnarchive?: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { setUpdates } = useLocalUpdates();
  return d.archived ? (
    <ButtonWithPromise
      variant="outlined"
      size="small"
      color="default"
      pending={t('Unarchiving...')}
      onClick={async () => {
        onUnarchive?.();
        // Strangely, we can't perform this after the promise - it doesn't register correctly.
        setUpdates((updates) => {
          const nextUpdates = updateMarkedAsRead(
            updates,
            instanceAccessToken,
            d.templateId,
            false
          );
          console.log('nextUpdates', nextUpdates);
          return nextUpdates;
        });
        void archiveMessage(instanceAccessToken, d.templateId, false);
      }}
    >
      {t('Unarchive')}
    </ButtonWithPromise>
  ) : (
    <ButtonWithPromise
      variant="contained"
      size="small"
      color="primary"
      pending={t('Archiving...')}
      onClick={async () => {
        onArchive?.();
        // Strangely, we can't perform this after the promise - it doesn't register correctly.
        setUpdates((updates) => {
          const nextUpdates = updateMarkedAsRead(
            updates,
            instanceAccessToken,
            d.templateId,
            true
          );
          console.log('nextUpdates', nextUpdates);
          return nextUpdates;
        });
        void archiveMessage(instanceAccessToken, d.templateId, true);
      }}
    >
      {t('Archive')}
    </ButtonWithPromise>
  );
};

export const MessageCard = ({
  d,
  instanceAccessToken,
  archiveControls,
  alwaysExpand
}: {
  d: IExperienceMessageDenormalized;
  instanceAccessToken: string;
  archiveControls?: boolean;
  alwaysExpand?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const [expanded, setExpanded] = useState(!!alwaysExpand);

  const onClick = () => {
    trackEvent(
      expanded
        ? {
            eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
            name: 'Message Card Collapsed',
            Explanation: 'User collapsed a message',
            'Message Template ID': d.templateId,
            'Message Title': d.subject
          }
        : {
            eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
            name: 'Message Card Expanded',
            Explanation: 'User expanded a message',
            'Message Template ID': d.templateId,
            'Message Title': d.subject
          }
    );
    setExpanded((x) => !x);
  };

  const archiveButton =
    archiveControls && expanded ? (
      <MessageArchiveButton
        d={d}
        instanceAccessToken={instanceAccessToken}
        onArchive={() => {
          trackEvent({
            eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
            name: 'Message Archived',
            Explanation: 'User archived a message in the status navigation',
            'Days Since Message Sent': Math.abs(
              moment().diff(toMoment(d.sendAt), 'days')
            ),
            'Message Template ID': d.templateId,
            'Message Title': d.subject
          });
          archiveMessage(instanceAccessToken, d.templateId, true);
        }}
        onUnarchive={() => {
          trackEvent({
            eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
            name: 'Message Unarchived',
            Explanation:
              'User removed a message from archive in the status navigation',
            'Days Since Message Sent': Math.abs(
              moment().diff(toMoment(d.sendAt), 'days')
            ),
            'Message Template ID': d.templateId,
            'Message Title': d.subject
          });
          archiveMessage(instanceAccessToken, d.templateId, false);
        }}
      />
    ) : null;

  return (
    <EmployeeAppCard onClick={!expanded && !alwaysExpand ? onClick : undefined}>
      <FlexContainerVertical fullWidth>
        <FlexContainer fullWidth justifyContent="space-between" spacing={2}>
          <FlexContainer
            spacing={2}
            alignItems={expanded ? 'center' : 'flex-start'}
          >
            <RoundIconContainer>
              <Mail size={24} />
            </RoundIconContainer>
            {expanded ? (
              <Typography variant="h5">{d.subject}</Typography>
            ) : (
              <FlexContainerVertical fullWidth spacing={0.5}>
                <strong>{d.subject}</strong>
                <TruncatedContentSummary blocks={d.blocks} limit={100} />
              </FlexContainerVertical>
            )}
          </FlexContainer>
          <FlexContainer
            onClick={(ev) => {
              ev.stopPropagation();
              ev.preventDefault();
            }}
          >
            <WithBreakpoints
              lg={archiveButton}
              md={archiveButton}
              sm={archiveButton}
            />
            {!alwaysExpand && (
              <IconButton onClick={onClick}>
                {expanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </IconButton>
            )}
          </FlexContainer>
        </FlexContainer>
        <WithBreakpoints
          xs={
            archiveButton && (
              <FlexContainer justifyContent="flex-end" fullWidth>
                {archiveButton}
              </FlexContainer>
            )
          }
        />

        {expanded && (
          <FlexContainerVertical
            fullWidth
            spacing={2}
            paddingLeft={7.5}
            paddingRight={4}
          >
            <Typography variant="caption">
              {toMoment(d.sendAt).format('MMMM Do')}
            </Typography>
            <MessageBody d={d} />
          </FlexContainerVertical>
        )}
      </FlexContainerVertical>
    </EmployeeAppCard>
  );
};
